/**
 * Message Item - Vitreous Support message bubble with read receipts
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Avatar, Dropdown, Modal, Typography, Button } from 'antd';
import { CheckOutlined, UserOutlined, DownloadOutlined, FileOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { PresenceIndicator } from './PresenceIndicator';
import dayjs from 'dayjs';
import './MessageItem.css';

const { Text } = Typography;

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|svg)(\?.*)?$/i;
const PDF_EXT = /\.pdf(\?.*)?$/i;

const getAttachmentUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const hostname = window.location.hostname.toLowerCase();
  const isProduction =
    hostname === 'patelhrms.com' ||
    hostname.includes('patelhrms.com') ||
    hostname === 'vitreous.support' ||
    hostname.includes('vitreous.support') ||
    hostname === 'www.vitreous.support';
  const API_ORIGIN = isProduction ? window.location.origin : 'http://localhost:5000';
  const path = url.startsWith('/') ? url : `/${url}`;
  return `${API_ORIGIN}${path}`;
};

interface MessageItemProps {
  message: {
    id: string;
    channelId: string;
    userId: string;
    text: string;
    contentType: string;
    attachmentUrl?: string;
    attachmentName?: string;
    threadCount: number;
    createdAt: string;
    updatedAt?: string;
    isEdited?: boolean;
    isDeleted?: boolean;
    reactions?: Array<{
      emoji: string;
      count: number;
      userIds: string[];
      hasUserReaction?: boolean;
    }>;
    user: { id: string; firstName: string; lastName: string; email: string };
  };
  showAvatar: boolean;
  userPresence?: { status: 'online' | 'away' | 'busy' | 'offline' };
  isRead?: boolean;
  canDeleteMessage?: boolean;
  onReply?: () => void;
  onEdit?: (messageId: string, currentText: string) => void;
  onDelete?: () => void;
  onPin?: () => void;
  onOpenThread?: (messageId: string) => void;
  onReaction?: (messageId: string, emoji: string) => void;
}

export const MessageItem: React.FC<MessageItemProps> = ({
  message,
  showAvatar,
  userPresence,
  isRead = false,
  canDeleteMessage = false,
  onReply,
  onEdit,
  onDelete,
  onPin,
  onOpenThread,
  onReaction,
}) => {
  const { user } = useAuth();
  const isOwnMessage = user?.id === message.userId;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const LONG_PRESS_MS = 500;

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  }, []);

  const handlePressStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if ((e.target as HTMLElement).closest('.message-attachment-btn')) return;
    clearLongPressTimer();
    longPressTimer.current = setTimeout(() => {
      longPressTimer.current = null;
      setMenuOpen(true);
    }, LONG_PRESS_MS);
  }, [clearLongPressTimer]);

  const handlePressEnd = useCallback(() => {
    clearLongPressTimer();
  }, [clearLongPressTimer]);

  useEffect(() => () => clearLongPressTimer(), [clearLongPressTimer]);

  const fullUrl = getAttachmentUrl(message.attachmentUrl);
  const fileName = message.attachmentName || 'Attachment';
  const isImage = IMAGE_EXT.test(fileName);
  const isPdf = PDF_EXT.test(fileName);

  const handleDownload = () => {
    if (!fullUrl) return;
    const a = document.createElement('a');
    a.href = fullUrl;
    a.download = fileName;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (message.isDeleted) {
    return (
      <div className="message-bubble-wrapper deleted">
        <div className="message-bubble message-deleted">
          <Typography.Text type="secondary" italic>This message was deleted.</Typography.Text>
        </div>
      </div>
    );
  }

  const formatTime = (ts: string) => dayjs(ts).format('h:mm A');

  const ReadReceipt: React.FC = () => {
    if (!isOwnMessage) return null;
    return (
      <span className="message-read-receipt" title={isRead ? 'Read' : 'Sent'}>
        {isRead ? (
          <span className="receipt-double">
            <CheckOutlined /><CheckOutlined />
          </span>
        ) : (
          <CheckOutlined className="receipt-sent" />
        )}
      </span>
    );
  };

  const menuItems = [
    ...(onEdit ? [{ key: 'edit', label: 'Edit', onClick: () => onEdit(message.id, message.text) }] : []),
    ...(canDeleteMessage && onDelete
      ? [{ key: 'delete', label: 'Delete message', danger: true, onClick: onDelete }]
      : []),
  ];

  const bubbleContent = (
    <div
      className={`message-bubble ${isOwnMessage ? 'own' : 'other'}`}
      {...(isOwnMessage && {
        title: 'Press and hold for options',
        onMouseDown: handlePressStart,
        onMouseUp: handlePressEnd,
        onMouseLeave: handlePressEnd,
        onTouchStart: handlePressStart,
        onTouchEnd: handlePressEnd,
        onTouchCancel: handlePressEnd,
      })}
    >
      {!isOwnMessage && showAvatar && (
        <Text className="message-sender-name">
          {message.user.firstName} {message.user.lastName}
        </Text>
      )}
      <div className="message-bubble-content">
        {message.text && <div className="message-text">{message.text}</div>}
        {message.attachmentUrl && (
          <div className="message-attachment">
            {isImage && fullUrl ? (
              <button
                type="button"
                className="message-attachment-btn message-attachment-image"
                onClick={(e) => { e.stopPropagation(); setPreviewOpen(true); }}
              >
                <img src={fullUrl} alt={fileName} className="message-attachment-thumb" />
              </button>
            ) : (
              <button
                type="button"
                className="message-attachment-btn"
                onClick={(e) => { e.stopPropagation(); setPreviewOpen(true); }}
              >
                📎 {message.attachmentName || 'Attachment'}
              </button>
            )}
            <Modal
              title={fileName}
              open={previewOpen}
              onCancel={() => setPreviewOpen(false)}
              width={isImage ? 640 : 520}
              zIndex={10050}
              style={{ marginBottom: 80 }}
              footer={[
                <Button key="close" onClick={() => setPreviewOpen(false)}>Close</Button>,
                <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
                  Download
                </Button>,
              ]}
              destroyOnClose
            >
              <div className="message-attachment-preview">
                {isImage && fullUrl && (
                  <img src={fullUrl} alt={fileName} style={{ maxWidth: '100%', maxHeight: '50vh', objectFit: 'contain' }} />
                )}
                {isPdf && fullUrl && (
                  <iframe
                    src={fullUrl}
                    title={fileName}
                    style={{ width: '100%', height: '50vh', border: 'none' }}
                  />
                )}
                {!isImage && !isPdf && (
                  <div className="message-attachment-preview-other">
                    <FileOutlined style={{ fontSize: 48, color: '#bfbfbf', marginBottom: 12 }} />
                    <Text>{fileName}</Text>
                    <Text type="secondary" style={{ fontSize: 12, marginTop: 4 }}>
                      Preview not available. Use Download to save the file.
                    </Text>
                  </div>
                )}
              </div>
            </Modal>
          </div>
        )}
        <div className="message-meta">
          <span className="message-time">{formatTime(message.createdAt)}</span>
          {message.isEdited && (
            <span className="message-edited"> (edited)</span>
          )}
          <ReadReceipt />
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`message-bubble-wrapper ${isOwnMessage ? 'own' : 'other'}`}
    >
      {!isOwnMessage && showAvatar && (
        <div className="message-bubble-avatar">
          <PresenceIndicator status={userPresence?.status || 'offline'} size="small">
            <Avatar size={28} icon={<UserOutlined />} />
          </PresenceIndicator>
        </div>
      )}
      <div className={`message-bubble-container ${isOwnMessage ? 'own' : 'other'}`}>
        {isOwnMessage ? (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['contextMenu']}
            open={menuOpen}
            onOpenChange={setMenuOpen}
            placement="topRight"
            overlayStyle={{ zIndex: 10050 }}
          >
            {bubbleContent}
          </Dropdown>
        ) : (
          bubbleContent
        )}
        {message.reactions && message.reactions.length > 0 && (
          <div className="message-reactions">
            {message.reactions.map((r, i) => (
              <button
                key={i}
                className={`reaction-btn ${r.hasUserReaction ? 'active' : ''}`}
                onClick={() => onReaction?.(message.id, r.emoji)}
              >
                <span>{r.emoji}</span>
                {r.count > 1 && <span className="reaction-count">{r.count}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
